import { HttpErrorResponse } from '@angular/common/http';
import { MessageError } from './message-error';
import { environment } from 'src/environments/environment';
import { SessionStorageRepository } from 'src/app/contexts/shared/storage/session-storage.repository';
import { BcpSessionStorage } from '@bcp/js-storage';
import { CHATBOT_IDENTIFIERS, CHATBOT_STORAGE_KEY } from 'src/app/contexts/shared/constants/chatbot.constants';

export class ErrorMessageFactory {
  private sessionStorageRepository = new SessionStorageRepository(new BcpSessionStorage());
  
  getErrorByCode(error: HttpErrorResponse, url: string): MessageError {
    const status = error?.status;
    const detail = error?.error?.exceptionDetails?.[0]?.code || '';

    const catalog = [
      {
        handler: () => this.linkExpired(),
        match: (s: number, d: string) =>
          s === 409 && ['DP0005', 'DP0012', 'DP0013', 'DP0014'].includes(d),
      },
      {
        handler: () => this.serviceTemporarilyDisabled(),
        match: (s: number) => s === 503,
      },
      {
        handler: () => this.servicePostTimeOut(),
        match: (s: number, _: string, u: string) =>
          s === 504 && u.includes('customer-sessions'),
      },
      {
        handler: () => this.cardBlocked(),
        match: (s: number, d: string) => s === 409 && ['DP0011'].includes(d),
      },
      {
        handler: () => this.serviceActiveSession(),
        match: (s: number, d: string) => s === 409 && ['DP0016'].includes(d),
      },
    ];

    const match = catalog.find((item) => item.match(status, detail, url));

    let fallbackHandler: () => MessageError;

    if (url === 'data-sessions') {
      fallbackHandler = () => this.serviceErrorGeneral();
    } else {
      fallbackHandler = () => this.servicePostError();
    }

    return match ? match.handler() : fallbackHandler();
  }

  linkExpired(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Este link ya no funciona. Puede ser que ya lo hayas utilizado o que se cumplió su tiempo de vigencia. Regresa a Whatsapp y tu asesor continuará tu atención.'
      : 'Este link ya no funciona. Puede ser que ya lo hayas utilizado o que se cumplió su tiempo de vigencia. Por favor, iniciar sesión en el WhatsApp del BCP.';
    
    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con mi asesor' : 'Iniciar sesión en Whatsapp';

    return {
      urlImg: '/assets/svg/error_general.svg',
      altTxtImg: 'Link Expiro',
      title: 'El link ha expirado',
      description,
      textBtn,
      style: 'margin-top: 2rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  serviceErrorGeneral(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Tuvimos un problema y no podemos continuar. Regresa a Whatsapp y tu asesor continuará tu atención.'
      : 'Tenemos problemas y no podemos continuar. Por favor, vuelve a intentarlo en unos minutos.';
    
    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con mi asesor' : 'Chatea con un asesor';
    
    return {
      urlImg: '/assets/svg/error-service.svg',
      altTxtImg: 'Error del servicio',
      title: 'Algo no ha salido bien',
      description,
      textBtn,
      style: 'top: 20%',
      redirectTo: `https://wa.me/${environment.NumberWsp}?text=Deseo%20hablar%20con%20un%20asesor`,
    };
  }

  servicePostError(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Tuvimos un problema y no podemos continuar. Regresa a Whatsapp y tu asesor continuará tu atención.'
      : 'Tenemos problemas y no podemos continuar. Por favor, vuelve a intentarlo en unos minutos.';
    
    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con mi asesor' : 'Chatea con un asesor';  

    return {
      urlImg: '/assets/svg/error-service.svg',
      altTxtImg: 'Algo no ha salido bien',
      title: 'Algo no ha salido bien',
      description,
      textBtn,
      style: 'margin-top: 8rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  servicePostTimeOut(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Tu asesor continuará tu atención desde ahí.'
      : 'Por favor, verifica la respuesta a tu consulta en el chat del BCP.';

    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con asesor' : 'Ir a WhatsApp';

    return {
      urlImg: '/assets/svg/success_img.svg',
      altTxtImg: 'Ahora vuelve a Whatsapp',
      title: 'Ahora vuelve a Whatsapp',
      description,
      textBtn,
      style: 'margin-top: 8rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  sessionExpired(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Por tu seguridad, cerramos esta sesión. Regresa a Whatsapp y tu asesor continuará tu atención.'
      : 'Por tu seguridad, vuelve a iniciar sesión a través del WhatsApp del BCP.';
    
    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con mi asesor' : 'Ir a WhatsApp';
    return {
      urlImg: '/assets/svg/error-timeout.svg',
      altTxtImg: 'Sesion expirada',
      title: 'El tiempo se ha agotado',
      description,
      textBtn,
      style: 'margin-top: 7rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  attempsPassed(): MessageError {
    return {
      urlImg: '/assets/svg/error-pwd.svg',
      altTxtImg: 'Error de password',
      title: 'Superaste los intentos permitidos',
      description: 'Por seguridad, tu tarjeta ha sido bloqueada por 24 horas.',
      textBtn: 'Entendido',
      style: 'margin-top: 5rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  cardBlocked(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const title = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Superaste el número de intentos de ingreso'
      : 'Tu tarjeta está bloqueada';

    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Por tu seguridad, no podrás validar tu identidad hasta mañana. Regresa a Whatsapp y tu asesor continuará tu atención.'
      : 'Intentaste ingresar con una tarjeta que está bloqueada. Inténtalo nuevamente mañana.';

    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con asesor' : 'Entendido';

    return {
      urlImg: '/assets/svg/error-pwd.svg',
      altTxtImg: 'Tarjeta bloqueada',
      title,
      description,
      textBtn,
      style: 'top: 20%',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  serviceErrorSendData(): MessageError {
    return {
      urlImg: '/assets/svg/error_general.svg',
      altTxtImg: 'Error del servicio',
      title: 'Algo no ha salido bien',
      description:
        'Tenemos problemas y no podemos continuar. Por favor, vuelve a intentarlo en unos minutos.',
      textBtn: 'Habla con asesor por Whatsapp',
      style: 'margin-top: 5rem',
      redirectTo: null,
    };
  }

  serviceErrorGetAuthentication(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con mi asesor' : 'Salir';

    return {
      urlImg: '/assets/svg/error_authentication.svg',
      altTxtImg: 'Error del servicio',
      title: 'Ocurrio un error inesperado',
      description:
        'Estamos trabajando para solucionarlo lo antes posible. Vuelve a intentarlo más tarde.',
      textBtn,
      style: 'margin-top: 5rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}?text=Deseo%20hablar%20con%20un%20asesor`,
    };
  }

  attempsPassedBiom(): MessageError {
    return {
      urlImg: '/assets/svg/error-service.svg',
      altTxtImg: 'Error del servicio',
      title: 'Algo no ha salido bien',
      description:
        'No has podido validar tu identidad. Por favor, vuelve a intentarlo.',
      textBtn: 'Volver a intentarlo',
      style: 'margin-top: 5rem',
      redirectTo: null,
    };
  }

  serviceErrorGetData(): MessageError {
    return {
      urlImg: '/assets/svg/error-service.svg',
      altTxtImg: 'Error del servicio',
      title: 'Algo no ha salido bien',
      description:
        'Tenemos problemas y no podemos continuar. Por favor, vuelve a intentarlo en unos minutos.',
      textBtn: 'Chatea con un asesor',
      style: 'margin-top: 5rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}?text=Deseo%20hablar%20con%20un%20asesor`,
    };
  }

  serviceErrorReceptApi(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con mi asesor' : 'Iniciar sesión en Whatsapp';

    return {
      urlImg: '/assets/svg/error_api.svg',
      altTxtImg: 'Error del servicio',
      title: 'Algo no ha salido bien',
      description:
        'Tenemos problemas y no podemos continuar. Por favor, vuelve a intentarlo en unos minutos.',
      textBtn,
      style: 'margin-top: 5rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  serviceTemporarilyDisabled(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const title = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Algo no ha salido bien'
      : 'Ocurrió un error inesperado';
    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Tuvimos un problema con nuestro sistema. Regresa a WhatsApp y tu asesor continuará tu atención.'
      : 'Tuvimos un problema con nuestro sistema. Por favor, vuelve a intentarlo en unos minutos.';
    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con asesor' : 'Entendido';

    return {
      urlImg: '/assets/svg/shs_conection_l.svg',
      altTxtImg: 'Servicio está temporalmente inhabilitado',
      title,
      description,
      textBtn,
      style: 'top: 20%',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  serviceActiveSession(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;
    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Regresa a Whatsapp y tu asesor continuará tu atención'
      : 'Regresa a whatsApp para continuar con tus consultas';

    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con asesor' : 'Ir a WhatsApp';

    return {
      urlImg: '/assets/svg/active_session.svg',
      altTxtImg: 'Error del servicio',
      title: 'Ya tienes una sesión activa',
      description,
      textBtn,
      style: 'margin-top: 5rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }
  
  linkError(): MessageError {
  const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;
    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Puede deberse a que lo has utilizado o ha caducado.'
      : 'Puede deberse a que lo has utilizado o ha caducado. Por favor, inicia sesión nuevamente.';
    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con asesor' : 'Iniciar sesión en Whatsapp';

    return {
      urlImg: '/assets/svg/error_link_expired.svg',
      altTxtImg: 'Link Expiro',
      title: 'El link no está disponible',
      description,
      textBtn,
      style: 'margin-top: 2rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }

  errorConnection(): MessageError {
    const chatbotId = this.sessionStorageRepository.find(CHATBOT_STORAGE_KEY) ?? CHATBOT_IDENTIFIERS.AS_IS;

    const description = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION
      ? 'Tuvimos un problema con nuestro sistema. Regresa a Whatsapp y tu asesor continuará tu atención.'
      : 'Tuvimos un error con nuestro sistema. Por favor, vuelve a intentarlo en unos minutos.';
    const textBtn = chatbotId === CHATBOT_IDENTIFIERS.ADVISOR_AUTHENTICATION ? 'Regresar con asesor' : 'Entendido';

    return {
      urlImg: '/assets/svg/error_connection.svg',
      altTxtImg: 'Error del servicio',
      title: 'Ocurrio un error inesperado',
      description,
      textBtn,
      style: 'margin-top: 5rem',
      redirectTo: `https://wa.me/${environment.NumberWsp}`,
    };
  }
}
